/**
 * Where a layout wants its videos now, read while a pass runs: a draw is awaited, and the layout may move on meanwhile.
 * `I` names a person's video (an identity, a user id); `R` is whatever draws it.
 */
export interface VideoSlotLayout<I, R> {
  /** Whose video each slot wants now, by slot. */
  wanted(): ReadonlyMap<string, I>;
  /** The element a slot is drawn into now; none while the layout has no place for it. */
  host(slot: string): HTMLElement | undefined;
  /** What draws now; with none nothing is drawn, and what was drawn is dropped. */
  renderer(): R | null;
}

/** How a renderer draws one slot's video into its element, and drops it again. */
export interface VideoSlotDrawing<I, R> {
  /** Draws `identity`'s video for `slot` into `host`; false when it could not be drawn (yet). */
  draw(renderer: R, slot: string, identity: I, host: HTMLElement): Promise<boolean>;
  drop(renderer: R, slot: string, identity: I, host: HTMLElement): Promise<void>;
  /**
   * MEET-E-48-02: whether the video drawn for `slot` into `host` has died since (it shows nothing): the pass drops it and
   * draws it again. A drawing without it keeps what it drew until the layout moves on.
   */
  lost?(renderer: R, slot: string, identity: I, host: HTMLElement): boolean;
}

/** A video a pass drew: whose, into which element, and by which renderer. */
interface Drawn<I, R> {
  identity: I;
  host: HTMLElement;
  renderer: R;
}

/**
 * MEET-E-45-05 (D4) — the one place a layout's videos are kept drawn, shared by the calls' compact view (ITP-E-185-01, where
 * it was `place()`) and the Meet room. Each pass drops whatever the layout no longer has where it was drawn (another person,
 * another element, another renderer), then draws what is missing, each into the element there is when it arrives. A slot the
 * renderer could not draw stays undrawn — its person keeps the initials or the avatar — and is asked again on the next pass
 * (ITP-E-189-02). A draw that lands after the layout moved on is dropped at once. MEET-E-48-02: a draw that succeeded and then
 * died (the drawing's `lost`) is dropped and drawn again on the next pass, as a slot the layout moved would be.
 *
 * Passes must not overlap; each caller runs them one after another (the call view's `staging` chain, the room's event queue).
 */
export class VideoSlotReconciler<I = string, R = unknown> {
  /** What was drawn, by slot. */
  private readonly drawn = new Map<string, Drawn<I, R>>();
  /** The slots wanted but not drawn: the renderer could not draw them. */
  private readonly undrawn = new Set<string>();

  constructor(private readonly drawing: VideoSlotDrawing<I, R>) {
  }

  /** Whether the last pass left `slot` drawn. */
  isDrawn(slot: string): boolean {
    return this.drawn.has(slot);
  }

  /** Whether `slot` is wanted and the renderer could not draw it. */
  isUndrawn(slot: string): boolean {
    return this.undrawn.has(slot);
  }

  async sync(layout: VideoSlotLayout<I, R>): Promise<void> {
    const wanted = layout.wanted();
    [...this.undrawn].filter(slot => !wanted.has(slot)).forEach(slot => this.undrawn.delete(slot));
    for (const [slot, current] of [...this.drawn]) {
      if (wanted.get(slot) !== current.identity || layout.host(slot) !== current.host || layout.renderer() !== current.renderer
        || this.drawing.lost?.(current.renderer, slot, current.identity, current.host)) {
        this.drawn.delete(slot);
        await this.drawing.drop(current.renderer, slot, current.identity, current.host);
      }
    }
    const renderer = layout.renderer();
    if (!renderer) {
      return;
    }
    for (const [slot, identity] of wanted) {
      const host = layout.host(slot);
      if (!host || this.drawn.has(slot)) {
        continue;
      }
      if (!await this.drawing.draw(renderer, slot, identity, host)) {
        this.undrawn.add(slot);
        continue;
      }
      this.undrawn.delete(slot);
      if (layout.host(slot) !== host || layout.wanted().get(slot) !== identity || this.drawn.has(slot) || layout.renderer() !== renderer) {
        await this.drawing.drop(renderer, slot, identity, host); // the layout moved on while it drew
        continue;
      }
      this.drawn.set(slot, {identity, host, renderer});
    }
  }
}
